import {useCallback, useEffect, useMemo, useState} from 'react';
import {ColorMode} from '../colorMode/ColorMode.enum';
import {LOCAL_STORAGE_COLOR_MODE_KEY} from '../colorMode/LocalStorageColorModeKey.constant';
import {IS_CLIENT_SIDE} from './shared/utils/constants';

export function useColorMode() {
  const [storageColorMode, setStorageColorMode] = useState<ColorMode>();

  /**
   * matchMedia는 객체를 반환하는데 이 객체는 매번 다른 객체를 반환해서 메모이징합니다.
   * 이벤트 등록, 제거시 매번 객체가 다르면 이벤트가 여러번 등록되는 케이스가 있기 때문입니다.
   */
  const darkModeMediaQuery = useMemo<MediaQueryList | undefined>(
    () =>
      IS_CLIENT_SIDE
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : undefined,
    []
  );

  const getSystemColorMode = useCallback(() => {
    return darkModeMediaQuery?.matches ? ColorMode.Dark : ColorMode.Light;
  }, [darkModeMediaQuery]);

  const setColorMode = useCallback(
    ({colorMode, className}: {colorMode: ColorMode; className?: ColorMode}) => {
      setStorageColorMode(colorMode);
      window.localStorage.setItem(LOCAL_STORAGE_COLOR_MODE_KEY, colorMode);
      document.body.classList.remove(ColorMode.Dark, ColorMode.Light);
      document.body.classList.add(className ?? colorMode);
    },
    []
  );

  useEffect(() => {
    setStorageColorMode(
      (localStorage?.getItem(LOCAL_STORAGE_COLOR_MODE_KEY) as ColorMode) ??
        ColorMode.Light
    );
  }, []);

  const listener = useCallback(() => {
    if (
      (localStorage?.getItem(LOCAL_STORAGE_COLOR_MODE_KEY) as ColorMode) ===
      ColorMode.System
    ) {
      const systemColor = getSystemColorMode();
      setColorMode({colorMode: ColorMode.System, className: systemColor});
    }
  }, [getSystemColorMode, setColorMode]);

  const removeListener = useCallback(() => {
    if (darkModeMediaQuery?.removeEventListener) {
      darkModeMediaQuery.removeEventListener('change', listener);
    } else if (darkModeMediaQuery?.removeListener) {
      darkModeMediaQuery.removeListener(listener);
    }
  }, [darkModeMediaQuery, listener]);

  useEffect(() => {
    let isColorModeSet = false;
    if (
      (localStorage?.getItem(LOCAL_STORAGE_COLOR_MODE_KEY) as ColorMode) ===
      ColorMode.System
    ) {
      isColorModeSet = true;
      // XXX: 미디어쿼리의 addEventListener, removeEventListener 는 ios14 부터 지원이라 저사양 폰을 위한 폴백으로 addListener, removeListener 를 지정합니다  (참고 : https://www.designcise.com/web/tutorial/how-to-fix-the-javascript-typeerror-matchmedia-removeeventlistener-is-not-a-function)
      if (darkModeMediaQuery?.addEventListener) {
        darkModeMediaQuery.addEventListener('change', listener);
      } else if (darkModeMediaQuery?.addListener) {
        darkModeMediaQuery.addListener(listener);
      }
    } else {
      removeListener();
    }

    return () => {
      if (isColorModeSet) {
        removeListener();
      }
    };
  }, [
    darkModeMediaQuery,
    getSystemColorMode,
    listener,
    removeListener,
    setColorMode,
    storageColorMode,
  ]);

  const toggleColorMode = useCallback(() => {
    setColorMode({
      colorMode:
        storageColorMode === ColorMode.Dark ? ColorMode.Light : ColorMode.Dark,
    });
  }, [setColorMode, storageColorMode]);

  const changeColorMode = useCallback(
    (colorMode: ColorMode) => {
      if (colorMode !== ColorMode.System) {
        setColorMode({colorMode});
        return;
      }
      const systemColorMode = getSystemColorMode();
      setColorMode({colorMode, className: systemColorMode});
    },
    [getSystemColorMode, setColorMode]
  );

  return {storageColorMode, toggleColorMode, changeColorMode};
}
